import type { Channel, Partner } from "@expert/shared-types";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { SubscriptionStatus } from "./websocket/types";

export interface GaiaStore {
    isWsAuthenticated: boolean;
    setIsWsAuthenticated: (isWsAuthenticated: boolean) => void;

    subscriptionStatus: Record<string, SubscriptionStatus>;
    setSubscriptionStatus: (sessionId: string, subscriptionStatus: SubscriptionStatus) => void;

    partners: Record<string, Partner>;
    setPartner: (sessionId: string, partner: Partner) => void;

    sessionTypes: Record<string, Channel>;
    setSessionType: (sessionId: string, sessionType: Channel) => void;
    getSessionType: (sessionId: string) => Channel | undefined;
    removeSessionType: (sessionId: string) => void;
}

export const useGaiaStore = create<GaiaStore>()(
    subscribeWithSelector(
        immer(
            devtools(
                (set, get) => ({
                    isWsAuthenticated: false,
                    setIsWsAuthenticated: (isWsAuthenticated: boolean) => {
                        set(
                            (state) => {
                                state.isWsAuthenticated = isWsAuthenticated;
                            },
                            false,
                            "setIsWsAuthenticated",
                        );
                    },
                    subscriptionStatus: {},
                    setSubscriptionStatus: (sessionId: string, subscriptionStatus: SubscriptionStatus) => {
                        set(
                            (state) => {
                                state.subscriptionStatus[sessionId] = subscriptionStatus;
                            },
                            false,
                            "setSubscriptionStatus",
                        );
                    },
                    partners: {},
                    setPartner: (sessionId: string, partner: Partner) => {
                        set(
                            (state) => {
                                state.partners[sessionId] = partner;
                            },
                            false,
                            "setPartner",
                        );
                    },
                    sessionTypes: {},
                    setSessionType: (sessionId: string, sessionType: Channel) => {
                        set(
                            (state) => {
                                state.sessionTypes[sessionId] = sessionType;
                            },
                            false,
                            "setSessionType",
                        );
                    },
                    getSessionType: (sessionId: string) => {
                        return get().sessionTypes[sessionId];
                    },
                    removeSessionType: (sessionId: string) => {
                        set(
                            (state) => {
                                delete state.sessionTypes[sessionId];
                            },
                            false,
                            "removeSessionType",
                        );
                    },
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "gaia",
                    name: "expert/gaia",
                },
            ),
        ),
    ),
);
